// src/pages/Acoes/components/PlanoAcaoDialog.jsx
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { criarPlanoAcao, atualizarPlanoAcao } from '../../../infra/api/PlanosAcao';
import { mensagemErro } from '../../../infra/api/Coletas';
import { deInputDataHora, paraInputDataHora } from '../../../utils/coleta';

const VAZIO = { titulo: '', motivo: '', responsavel: '', prazo: '', instrucao: '' };

/**
 * Nova ação (pode nascer de uma amostra: `amostra` = o "antes") ou edição de uma ação
 * em aberto (`item`).
 */
export default function PlanoAcaoDialog({ open, onClose, item, amostra, onSalvo }) {
    const editando = Boolean(item);
    const [form, setForm] = useState(VAZIO);
    const [antes, setAntes] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!open) return;
        setErro('');
        if (item) {
            const p = item.plano;
            setForm({
                titulo: p.titulo || '',
                motivo: p.motivo || '',
                responsavel: p.responsavel || '',
                prazo: p.prazo ? paraInputDataHora(new Date(p.prazo)) : '',
                instrucao: p.instrucao || '',
            });
            setAntes(item.antes ? { id: item.antes.amostraId, rotulo: item.antes.identificacao } : null);
        } else {
            setForm(VAZIO);
            setAntes(amostra ? { id: amostra.id, rotulo: amostra.identificacao } : null);
        }
    }, [open, item, amostra]);

    const campo = (nome) => ({
        value: form[nome],
        onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.value })),
    });

    const salvar = async () => {
        if (!form.titulo.trim()) {
            setErro('Informe o título da ação.');
            return;
        }
        const prazo = form.prazo ? deInputDataHora(form.prazo) : null;
        if (form.prazo && !prazo) {
            setErro('Prazo inválido.');
            return;
        }
        const dados = {
            titulo: form.titulo.trim(),
            motivo: form.motivo.trim() || null,
            responsavel: form.responsavel.trim() || null,
            prazo: prazo ? prazo.toISOString() : null,
            instrucao: form.instrucao.trim() || null,
            amostraAntesId: antes?.id || null,
            pagina: amostra?.pagina || item?.plano?.pagina || null,
        };
        setSalvando(true);
        setErro('');
        try {
            const salvo = editando ? await atualizarPlanoAcao(item.plano.id, dados) : await criarPlanoAcao(dados);
            onSalvo(salvo);
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível salvar a ação.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pr: 6 }}>
                {editando ? 'Editar ação' : 'Nova ação'}
                <IconButton onClick={onClose} disabled={salvando} aria-label="Fechar" sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {erro && <Alert severity="error">{erro}</Alert>}
                    {antes && (
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lucide.FlaskConical size={16} />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Amostra que motivou a ação: <strong>{antes.rotulo}</strong>
                            </Typography>
                            {!editando && (
                                <Button size="small" onClick={() => setAntes(null)}>Remover</Button>
                            )}
                        </Box>
                    )}
                    <TextField label="O que fazer" placeholder="Ex.: Verificar higienização da linha e do tanque" required {...campo('titulo')} inputProps={{ maxLength: 120 }} autoFocus />
                    <TextField label="Motivo" placeholder="Ex.: Aumento observado entre os pontos do último ciclo de coleta" {...campo('motivo')} inputProps={{ maxLength: 200 }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField label="Responsável" {...campo('responsavel')} inputProps={{ maxLength: 80 }} fullWidth />
                        <TextField label="Prazo" type="datetime-local" InputLabelProps={{ shrink: true }} {...campo('prazo')} fullWidth />
                    </Stack>
                    <TextField label="Como fazer" multiline minRows={3} {...campo('instrucao')} inputProps={{ maxLength: 1000 }} />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={salvando}>Cancelar</Button>
                <Button variant="contained" disableElevation onClick={salvar} disabled={salvando} sx={{ borderRadius: 2 }}>
                    {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Criar ação'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
